import React, { useCallback, useMemo, useState } from 'react';
import styles from './VideoCreationsCharts.module.css';
import { formatNumberToCommaNotation } from '../../../../../../../helpers';
import LineChart, { Point } from '../../../../../../../ui/DataDisplay/Charts/LineChart/LineChart';
import Tabs, { TabNav, TabNavItem, TabPanel, TabPanels } from '../../../../../../../ui/DataDisplay/Tabs/Tabs';
import { TiktokAudioDailyStats } from '@round/api';

const now = new Date();
now.setHours(23, 59, 59);
const twoWeeksAgo = new Date(now.getTime() - 1000 * 60 * 60 * 24 * 7 * 2);
twoWeeksAgo.setHours(0, 0, 0, 0);

type Props = {
    creationsStats: TiktokAudioDailyStats[];
    loading: boolean;
    errorLoading: boolean;
};

const VideoCreationsCharts = ({ creationsStats, errorLoading, loading }: Props) => {
    const [activeTab, setActiveTab] = useState('totalCreations');
    const [dailyStatsDateFrom, setDailyStatsDateFrom] = useState<Date>(twoWeeksAgo);
    const [dailyStatsDateTo, setDailyStatsDateTo] = useState<Date>(now);

    const isWithinDateRange = useCallback(
        (stats: TiktokAudioDailyStats) => {
            const date = Date.parse(stats.timestamp);
            return date >= dailyStatsDateFrom.getTime() && date <= dailyStatsDateTo.getTime();
        },
        [dailyStatsDateFrom, dailyStatsDateTo]
    );

    const totalCreationsPoints: Point[] = useMemo(() => {
        return creationsStats
            .filter(isWithinDateRange)
            .map((stats: TiktokAudioDailyStats): Point => ({ x: stats.timestamp, y: stats.video_count }));
    }, [creationsStats, isWithinDateRange]);

    const dailyCreationsPoints: Point[] = useMemo(() => {
        return creationsStats
            .filter(isWithinDateRange)
            .map((stats: TiktokAudioDailyStats): Point => ({ x: stats.timestamp, y: stats.change }));
    }, [creationsStats, isWithinDateRange]);

    const renderTooltip = useCallback(({ total, daily }: { total?: number; daily?: number }) => {
        return (
            <div className={styles.tooltipContent}>
                <span className={styles.tooltipDot} />
                <div>
                    {typeof total !== 'undefined' && (
                        <p>
                            Total Creations:
                            <span className={styles.tooltipValue}>{formatNumberToCommaNotation(total)}</span>
                        </p>
                    )}
                    {typeof daily !== 'undefined' && (
                        <p>
                            Daily Creations:
                            <span className={styles.tooltipValue}>{formatNumberToCommaNotation(daily)}</span>
                        </p>
                    )}
                </div>
            </div>
        );
    }, []);

    const renderTotalCreationsTooltip = useCallback(
        (point: Point) => {
            const dailyCreations = dailyCreationsPoints.find((d) => d.x === point.x);
            return renderTooltip({ total: point.y, daily: dailyCreations?.y });
        },
        [dailyCreationsPoints, renderTooltip]
    );

    const renderDailyCreationsTooltip = useCallback(
        (point: Point) => {
            const totalCreations = totalCreationsPoints.find((t) => t.x === point.x);
            return renderTooltip({ total: totalCreations?.y, daily: point.y });
        },
        [renderTooltip, totalCreationsPoints]
    );

    return (
        <Tabs activeTab={activeTab} onChange={setActiveTab}>
            <TabNav>
                <TabNavItem name="totalCreations">Total creations</TabNavItem>
                <TabNavItem name="dailyCreations">Daily creations</TabNavItem>
            </TabNav>

            <TabPanels>
                <TabPanel name="totalCreations">
                    <LineChart
                        enableDateRange
                        loading={loading}
                        error={errorLoading}
                        title="Total creations"
                        points={totalCreationsPoints}
                        dateFrom={dailyStatsDateFrom}
                        onDateFromChange={setDailyStatsDateFrom}
                        dateTo={dailyStatsDateTo}
                        onDateToChange={setDailyStatsDateTo}
                        renderTooltip={renderTotalCreationsTooltip}
                    />
                </TabPanel>

                <TabPanel name="dailyCreations">
                    <LineChart
                        enableDateRange
                        beginAtZero
                        title="Daily creations"
                        points={dailyCreationsPoints}
                        loading={loading}
                        error={errorLoading}
                        dateFrom={dailyStatsDateFrom}
                        onDateFromChange={setDailyStatsDateFrom}
                        dateTo={dailyStatsDateTo}
                        onDateToChange={setDailyStatsDateTo}
                        renderTooltip={renderDailyCreationsTooltip}
                    />
                </TabPanel>
            </TabPanels>
        </Tabs>
    );
};

export default VideoCreationsCharts;
